import ReactJson from "@microlink/react-json-view";
import { ExternalLink,Maximize2 } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { ArtifactDataTableType, getArtifactTypeStyle } from "src/common/ArtifactUtils";
import { cn } from "src/common/utils";
import CodeBlock from "src/components/CodeBlock";
import { Badge } from "src/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Markdown } from "src/components/ui/Markdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow} from "src/components/ui/table";
import { Tooltip } from "src/components/ui/tooltip";
import { ZoomableChartLazy as ZoomableChart } from "src/components/ui/ZoomableChart/lazy";
import { useTheme } from "src/context/ThemeContext";

import { ArtifactType } from "./__generated__/ArtifactCardQuery.graphql";


interface ArtifactCardProps {
  title: string;
  description: string;
  query: string;
  artifactType: ArtifactType;
  artifactValue: any;
  groupTags: readonly string[];
  count: number;
  isHighlighted?: boolean;
  showToolInfo?: boolean;
  sourceLink?: string;
  sourceName?: string;
  onRemoveHighlight?: (id: string) => void;
}

type ArtifactContent = {
  data?: any;
  labels?: string[];
  metadata?: {
    build_url?: string;
    run_url?: string;
    build_number?: number;
    run_id?: number;
    build_state?: string;
    run_conclusion?: string;
    build_branch?: string;
    branch?: string;
    build_commit?: string;
    commit?: string;
    pipeline_slug?: string;
    workflow_name?: string;
  };
  jobs?: Array<{
    name: string;
    state: string;
    logs: string;
    job_id?: string;
  }>;
};

const transformMetricsData = (data: any) => {
  const labels: string[] = [];
  const transformedData = data.map((series: any) => {
    labels.push(series.group_tags);
    return series.timestamps_ms.map((ts: any, index: any) => ({
      ts,
      events: series.values[index],
    }));
  });

  return {
    labels,
    data: transformedData,
  };
};

function sortObject(obj: Record<string, any>): Record<string, any> {
  return Object.keys(obj)
    .sort()
    .reduce((sorted, key) => {
      sorted[key] = obj[key];
      return sorted;
    }, {} as Record<string, any>);
}

const transformLogsData = (data: any[]) => {
  if (!Array.isArray(data)) {
    return {}; // Return empty object if data is not an array
  }

  // For regular logs with timestamp, use the original sorting logic
  const hasTimestamps = data.some(item => item && typeof item.timestamp === "string");

  if (hasTimestamps) {
    return [...data]
      .filter(item => item && typeof item.timestamp === "string")
      .sort((a, b) => a.timestamp!.localeCompare(b.timestamp!))
      .reduce<Record<string, any>>((acc, { message, timestamp, ...rest }, index) => {
        const safeTimestamp = timestamp || `unknown_${index}`;
        const key = acc[safeTimestamp] ? `${safeTimestamp}_${index}` : safeTimestamp;
        acc[key] = Object.keys(rest).length === 0 ? message : sortObject({ message, ...rest });
        return acc;
      }, {});
  }

  // If no timestamps but has structured data, just number them
  return data.reduce((acc, item, idx) => {
    acc[`${idx + 1}`] = item;
    return acc;
  }, {});
};

function ArtifactTypeBadge({ type }: { type: string }) {
  const typeInfo = getArtifactTypeStyle(type);
  const Icon = typeInfo.icon;

  return (
    <Badge variant="outline" className="text-xs font-normal px-2 py-1 h-auto bg-secondary border-border">
      <div className={cn("flex items-center", typeInfo.textColor)}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {typeInfo.label}
      </div>
    </Badge>
  );
}

export function SourceLink({ link, linkText, tooltip }: { link?: string; linkText?: string; tooltip?: string }) {
  if (!link || !linkText) return null;

  const linkElement = (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex"
    >
      <div className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-border bg-muted hover:bg-card hover:border-border text-muted-foreground hover:text-foreground transition-all">
        <ExternalLink className="h-3 w-3" /> {linkText}
      </div>
    </a>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip} position="top">
        {linkElement}
      </Tooltip>
    );
  }

  return linkElement;
}

export const ArtifactCard: React.FC<ArtifactCardProps> = ({
  isHighlighted = false,
  artifactType,
  artifactValue,
  groupTags,
  title,
  query,
  sourceLink,
  sourceName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const typeDisplayName = artifactType.toLowerCase();
  const { resolvedTheme } = useTheme();
  const jsonViewTheme = resolvedTheme === "dark" ? "twilight" : "rjv-default";

  // Detect CLI_OUTPUT with structured table rows from Snowflake parser
  const cliTableData = useMemo(() => {
    if (artifactType !== "CLI_OUTPUT" || !Array.isArray(artifactValue)) return null;
    const entry = artifactValue[0];
    if (entry?.rows && Array.isArray(entry.rows) && entry.headers) {
      return {
        rows: entry.rows as Record<string, unknown>[],
        headers: entry.headers as string[],
        totalRows: entry.total_rows as number | undefined,
        rowsTruncated: entry.rows_truncated === true || (typeof entry.total_rows === "number" && entry.total_rows > (entry.rows as unknown[]).length),
        textBeforeTable: entry.text_before_table as string | undefined,
      };
    }
    return null;
  }, [artifactType, artifactValue]);

  // Memoize transformed logs data to prevent ReactJson from resetting on every render
  const transformedLogsData = useMemo(() => {
    if (
      artifactType === "LOGS" ||
      artifactType === "COMMITS" ||
      artifactType === "ANNOTATION" ||
      artifactType === "EVENTS" ||
      artifactType === "DASHBOARDS" ||
      artifactType === "BUILDS" ||
      artifactType === "WORKFLOWS" ||
      artifactType === "MONITORS" ||
      artifactType === "ISSUES" ||
      artifactType === "SPANS" ||
      artifactType === "TRACES" ||
      artifactType === "MEMORIES" ||
      artifactType === "WEB" ||
      artifactType === "DATA" ||
      artifactType === "CHANNEL" ||
      artifactType === "THREADS" ||
      artifactType === "CLI_OUTPUT"
    ) {
      return transformLogsData(artifactValue);
    }
    return null;
  }, [artifactType, artifactValue]);

  // Separate style objects for preview and modal to prevent re-renders
  const previewStyle = useMemo(() => ({
    fontSize: "10px",
    wordBreak: "break-all" as const,
  }), []);

  const modalStyle = useMemo(() => ({
    fontSize: "12px",
    wordBreak: "break-all" as const,
  }), []);

  const getArtifactContent = (): ArtifactContent | null => {
    switch (artifactType) {
      case "METRICS": {
        return artifactValue.filter((value: any) => {
          // If there are no group tags, show all the lines
          if (groupTags.length > 0) {
            return groupTags.some((tag) => value.group_tags?.includes(tag));
          } else {
            return true;
          }
        });
      }
      case "TRACES": {
        return artifactValue;
      }
      case "TABLE": {
        return {
          data: artifactValue as ArtifactDataTableType,
          labels: [],
        };
      }
      case "BUILD_LOGS": {
        // BUILD_LOGS has metadata + jobs structure, return as-is
        if (!artifactValue || !artifactValue.jobs || artifactValue.jobs.length === 0) {
          return null;
        }
        return artifactValue;
      }
      case "LOGS":
      case "COMMITS":
      case "ANNOTATION":
      case "EVENTS":
      case "DASHBOARDS":
      case "BUILDS":
      case "WORKFLOWS":
      case "MONITORS":
      case "ISSUES":
      case "SPANS":
      case "MEMORIES":
      case "WEB":
      case "DATA":
      case "CHANNEL":
      case "THREADS":
      case "CLI_OUTPUT": {
        if (artifactValue.length === 0) {
          return null;
        }
        return artifactValue;
      }
      case "DOCUMENTS": {
        if (!artifactValue || !Array.isArray(artifactValue) || artifactValue.length === 0) {
          return null;
        }
        return {
          data: artifactValue,
          labels: [],
        };
      }
      default:
        return null;
    }
  };

  const handleMaximize = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsModalOpen(true);
  };

  const toggleRowExpansion = useCallback((rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  }, []);

  const renderContent = (artifactContent: ArtifactContent | null) => {
    switch (artifactType) {
      case "METRICS": {
        const { data, labels } = transformMetricsData(artifactContent);
        return (
          <div className="w-full h-full">
            <ZoomableChart
              data={data}
              labels={labels}
              isPreview={!isModalOpen}
              onExpand={isModalOpen ? undefined : handleMaximize}
            />
          </div>
        );
      }

      case "BUILD_LOGS": {
        // Render build logs with metadata + job logs
        if (!artifactContent || !artifactContent.jobs || artifactContent.jobs.length === 0) {
          return null;
        }

        return (
          <div className="w-full h-full overflow-auto">
            {/* Build metadata */}
            {artifactContent.metadata && (
              <div className="mb-4 p-3 bg-card/50 rounded border border-border">
                <div className="text-xs text-muted-foreground space-y-1">
                  {/* Build/Run URL */}
                  {(artifactContent.metadata.build_url || artifactContent.metadata.run_url) && (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Build:</span>
                      <a
                        href={artifactContent.metadata.build_url || artifactContent.metadata.run_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 flex items-center gap-1"
                      >
                        #{artifactContent.metadata.build_number || artifactContent.metadata.run_id}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  {/* State/Status */}
                  {(artifactContent.metadata.build_state || artifactContent.metadata.run_conclusion) && (
                    <div>
                      <span className="font-semibold">State:</span>{" "}
                      {artifactContent.metadata.build_state || artifactContent.metadata.run_conclusion}
                    </div>
                  )}

                  {/* Branch */}
                  {(artifactContent.metadata.build_branch || artifactContent.metadata.branch) && (
                    <div>
                      <span className="font-semibold">Branch:</span>{" "}
                      {artifactContent.metadata.build_branch || artifactContent.metadata.branch}
                    </div>
                  )}

                  {/* Commit */}
                  {(artifactContent.metadata.build_commit || artifactContent.metadata.commit) && (
                    <div>
                      <span className="font-semibold">Commit:</span>{" "}
                      <code className="text-xs bg-card px-1 py-0.5 rounded">
                        {(artifactContent.metadata.build_commit || artifactContent.metadata.commit)?.substring(0, 7)}
                      </code>
                    </div>
                  )}

                  {/* Pipeline/Workflow name */}
                  {(artifactContent.metadata.pipeline_slug || artifactContent.metadata.workflow_name) && (
                    <div>
                      <span className="font-semibold">Pipeline:</span>{" "}
                      {artifactContent.metadata.pipeline_slug || artifactContent.metadata.workflow_name}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Job logs */}
            {artifactContent.jobs.map((job: any, idx: number) => {
              // Determine badge color based on state
              const getStateBadgeClass = (state: string) => {
                const lowerState = state?.toLowerCase() || "";
                if (lowerState.includes("pass") || lowerState.includes("success")) {
                  return "bg-green-500/20 text-green-400 border-green-500/50";
                } else if (lowerState.includes("fail") || lowerState.includes("broken")) {
                  return "bg-red-500/20 text-red-400 border-red-500/50";
                } else if (lowerState.includes("pending") || lowerState.includes("running")) {
                  return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
                } else {
                  return "bg-slate-500/20 text-slate-400 border-slate-500/50";
                }
              };

              return (
                <div key={idx} className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-2">
                    <span className="text-xs font-semibold text-muted-foreground">{job.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${getStateBadgeClass(job.state)}`}>
                      {job.state}
                    </span>
                  </div>
                  <CodeBlock text={job.logs || "No logs available"} />
                </div>
              );
            })}
          </div>
        );
      }

      // CLI_OUTPUT: render as a table when structured rows are available
      case "CLI_OUTPUT": {
        if (cliTableData) {
          const { rows, headers, totalRows, rowsTruncated, textBeforeTable } = cliTableData;
          const displayRows = isModalOpen ? rows : rows.slice(0, 20);
          const hasMore = !isModalOpen && rows.length > 20;
          return (
            <div className={cn("w-full h-full max-w-full relative", !isModalOpen && "[&>div]:overflow-hidden")}>
              {textBeforeTable && (
                <div className="text-xs text-muted-foreground px-2 py-1 whitespace-pre-wrap">{textBeforeTable}</div>
              )}
              <Table className="table-auto w-max">
                <TableHeader>
                  <TableRow className="border-border">
                    {headers.map((header, colIdx) => (
                      <TableHead
                        key={colIdx}
                        className="text-xs font-semibold text-muted-foreground py-1 px-2 whitespace-nowrap"
                      >
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.map((row, rowIdx) => (
                    <TableRow
                      key={rowIdx}
                      className={cn(
                        "hover:bg-accent/50 border-border",
                        isModalOpen && "cursor-pointer"
                      )}
                      onClick={isModalOpen ? () => toggleRowExpansion(rowIdx) : undefined}
                    >
                      {headers.map((header, colIdx) => {
                        const val = row[header];
                        const display = val === null || val === undefined ? "NULL" : String(val);
                        const isExpanded = expandedRows.has(rowIdx);
                        return (
                          <TableCell
                            key={colIdx}
                            className={cn(
                              "text-xs py-1 px-2",
                              val === null || val === undefined
                                ? "text-muted-foreground italic"
                                : "text-foreground"
                            )}
                            title={display}
                          >
                            <div className={cn(
                              !isExpanded && "whitespace-nowrap overflow-hidden text-ellipsis"
                            )}>
                              {display}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {hasMore && (
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-secondary to-transparent pointer-events-none flex items-end justify-center pb-1">
                  <span className="text-xs text-muted-foreground">
                    {totalRows ? `${rows.length} of ${totalRows} rows` : `${rows.length} rows`} — click to expand
                  </span>
                </div>
              )}
              {isModalOpen && rowsTruncated && (
                <div className="text-xs text-muted-foreground mt-1 px-2 italic">
                  {totalRows ? `Showing ${rows.length} of ${totalRows} rows (truncated)` : `Showing first ${rows.length} rows (result set truncated)`}
                </div>
              )}
            </div>
          );
        }
        // Fall through to JSON renderer when no structured rows
        try {
          if (!artifactValue || !Array.isArray(artifactValue) || artifactValue.length === 0 || !transformedLogsData) {
            return null;
          }
          const isOutputTruncated = artifactValue[0]?.output_truncated === true;
          return (
            <div className="w-full h-full overflow-auto">
              <ReactJson
                key={isModalOpen ? "modal" : "preview"}
                src={transformedLogsData}
                theme={jsonViewTheme}
                collapsed={2}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
                quotesOnKeys={false}
                displayArrayKey={false}
                name={false}
                groupArraysAfterLength={100}
                style={isModalOpen ? modalStyle : previewStyle}
              />
              {isOutputTruncated && (
                <div className="text-xs text-muted-foreground mt-1 px-2 italic">
                  Output truncated
                </div>
              )}
            </div>
          );
        } catch {
          return null;
        }
      }

      // Reuse the 'logs' renderer for all JSON-based artifact types
      case "COMMITS":
      case "ANNOTATION":
      case "LOGS":
      case "EVENTS":
      case "DASHBOARDS":
      case "BUILDS":
      case "WORKFLOWS":
      case "MONITORS":
      case "ISSUES":
      case "SPANS":
      case "TRACES":
      case "MEMORIES":
      case "WEB":
      case "DATA":
      case "CHANNEL":
      case "THREADS":
        try {
          // If the data is invalid or empty
          if (!artifactValue || !Array.isArray(artifactValue) || artifactValue.length === 0 || !transformedLogsData) {
            return null;
          }
          return (
            <div className="w-full h-full overflow-auto">
              <ReactJson
                key={isModalOpen ? "modal" : "preview"}
                src={transformedLogsData}
                theme={jsonViewTheme}
                collapsed={2}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
                quotesOnKeys={false}
                displayArrayKey={false}
                name={false}
                groupArraysAfterLength={100}
                style={isModalOpen ? modalStyle : previewStyle}
              />
            </div>
          );
        } catch {
          return null;
        }

      case "DOCUMENTS": {
        const documents = artifactContent?.data;
        if (!documents || !Array.isArray(documents) || documents.length === 0) {
          return null;
        }

        const displayContent = isModalOpen ? documents : documents.slice(0, 5);

        return (
          <div className="w-full h-full overflow-auto px-2 py-2">
            <div className="space-y-2">
              {displayContent.map((doc: any, docIndex: number) => {
                const isExpanded = expandedRows.has(docIndex);

                return (
                  <div
                    key={docIndex}
                    className={cn(
                      "border border-border rounded-lg p-3 bg-card/50 hover:bg-card/70 transition-colors",
                      isModalOpen && "cursor-pointer"
                    )}
                    onClick={isModalOpen ? () => toggleRowExpansion(docIndex) : undefined}
                  >
                    {/* Title and URL */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {doc.url ? (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Ensure navigation happens even if parent has click handler
                              if (!doc.url) {
                                e.preventDefault();
                              }
                            }}
                            className="text-primary hover:text-primary/80 font-semibold text-xs flex items-center gap-1"
                          >
                            {doc.title || "Untitled Document"}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <div className="text-foreground font-semibold text-xs">
                            {doc.title || "Untitled Document"}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Content Preview/Full - only shown in modal */}
                    {isModalOpen && doc.content && (
                      <div className="mt-2">
                        {isExpanded ? (
                          <CodeBlock text={doc.content} plainText={true} />
                        ) : (
                          <div className="text-xs text-muted-foreground line-clamp-3">
                            {doc.content.length > 200 ? doc.content.substring(0, 200) + "..." : doc.content}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!isModalOpen && documents.length > 5 && (
                <div className="text-xs text-muted-foreground mt-2 italic px-3">
                  +{documents.length - 5} more document(s)
                </div>
              )}
            </div>
          </div>
        );
      }

      case "DATAFRAMES":
      case "TABLE": {
        const content = artifactValue as ArtifactDataTableType;
        const displayContent = isModalOpen ? content : content.slice(0, 5);
        return (
          <div className="w-full h-full overflow-auto px-2">
            <Table className="table-fixed">
              <TableBody>
                {displayContent.map((row: Record<string, any>, rowIndex: number) => {
                  const isExpanded = expandedRows.has(rowIndex);
                  return (
                    <TableRow
                      key={rowIndex}
                      className={cn(
                        "hover:bg-accent/50",
                        isModalOpen && "cursor-pointer"
                      )}
                      onClick={isModalOpen ? () => toggleRowExpansion(rowIndex) : undefined}
                    >
                      {Object.keys(row).map((columnName, colIndex) => (
                        <TableCell
                          key={colIndex}
                          className="text-xs text-foreground py-1 px-2 w-full"
                          title={String(row[columnName])}
                        >
                          <div className={cn(
                            !isExpanded && "overflow-hidden text-ellipsis whitespace-nowrap"
                          )}>
                            {row[columnName]}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const renderDialogContent = () => {
    // Determine if we should show the code block based on artifact type
    const shouldShowCodeBlock = artifactType === "METRICS" &&
                               query?.trim().length > 0;

    const codeBlock = shouldShowCodeBlock ? (
      <div className="mb-2">
        <CodeBlock text={query} />
      </div>
    ) : null;

    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ArtifactTypeBadge type={typeDisplayName} />
          <SourceLink link={sourceLink} linkText={sourceName} />
        </div>
        {codeBlock}
        <div className="bg-secondary border border-border rounded-lg flex-1 min-h-0 overflow-auto p-2">
          <div className="aspect-[16/9] w-full max-h-[70vh]">
            {renderContent(getArtifactContent())}
          </div>
        </div>
      </div>
    );
  };

  const artifactContent = getArtifactContent();
  if (!artifactContent) {
    return <div></div>;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          "w-full max-h-[16rem] rounded-lg overflow-hidden bg-secondary flex flex-col items-center border border-border relative p-1 cursor-pointer",
          isHighlighted ? "ring-2 ring-green-600" : "",
        )}
        onClick={handleMaximize}
      >
        <div
          className={cn(
            "flex-1 w-full flex items-center justify-center overflow-hidden bg-transparent transition-colors duration-200",
            isHighlighted ? "bg-white/[0.04]" : "",
          )}
        >
          {renderContent(artifactContent)}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
          <Maximize2 className="text-white/70 w-6 h-6" />
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent variant="full" className="flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl">
              <Markdown>{title}</Markdown>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              View detailed artifact content
            </DialogDescription>
          </DialogHeader>
          <div  >
            {renderDialogContent()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
